import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ChartBar, Warning, Copy, Trash, PencilSimple, Eye, CaretDown } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AvatarDisplay } from "@/components/Avatar";
import { StatusBadge } from "@/components/StatusBadge";
import { WriteTagButton } from "@/components/WriteTagButton";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";

const actionBtn = "sb-btn py-2.5 bg-white font-medium flex items-center justify-center gap-2 text-sm";
const AC = { copy: "#3b82f6", label: "#eab308", edit: "#14b8a6", stats: "#a855f7", preview: "#f97316", block: "#fb5a3c", del: "#ef4444" };

export function BuddyList({ tags, onChanged, canEdit = true, limit }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [blockId, setBlockId] = useState(null);
  const [glowKey, setGlowKey] = useState(null);
  const glow = (key) => { setGlowKey(key); setTimeout(() => setGlowKey((k) => (k === key ? null : k)), 650); };
  const gcls = (key) => (glowKey === key ? " sb-glow" : "");
  const swipe = React.useRef({});

  const url = (c) => `${window.location.origin}/t/${c}`;
  const copyUrl = async (c) => { await copyText(url(c)); toast.success(t("urlCopied")); };
  const confirmDelete = async () => { await api.delete(`/tags/${deleteId}`); setDeleteId(null); toast.success(t("deleteBuddy")); onChanged && onChanged(); };
  const confirmBlock = async () => { await api.post(`/tags/${blockId}/status`, { status: "BLOCKED" }); setBlockId(null); toast.success(t("buddyBlocked")); onChanged && onChanged(); };
  const unblock = async (id) => { await api.post(`/tags/${id}/status`, { status: "ACTIVE" }); toast.success(t("buddyUnblocked")); onChanged && onChanged(); };

  return (
    <div className="space-y-3">
      {(limit && !showAll ? tags.slice(0, limit) : tags).map((tg) => {
        const open = openId === tg.id;
        return (
          <div key={tg.id} className="sb-card overflow-hidden" data-testid={`buddy-card-${tg.code}`}
            onTouchStart={(e) => { swipe.current[tg.id] = e.touches[0].clientX; }}
            onTouchEnd={(e) => { const st = swipe.current[tg.id]; if (st != null && canEdit && e.changedTouches[0].clientX - st < -60) setDeleteId(tg.id); }}>
            <button type="button" data-testid={`buddy-toggle-${tg.code}`}
              onClick={() => setOpenId(open ? null : tg.id)}
              className="w-full p-4 flex items-center gap-3 text-left active:scale-[0.99] transition-transform">
              <AvatarDisplay type={tg.avatar_type} value={tg.avatar_value} size={48} className="shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading font-semibold text-gray-900 truncate">{tg.label || t("unnamed")}</p>
                  <StatusBadge status={tg.status} />
                </div>
                <p className="text-xs font-body text-gray-500 mt-1">
                  {tg.scans} {t("totalScans")} · {tg.calls} {t("totalCalls")} · {tg.messages} {t("totalMessages")}
                </p>
              </div>
              <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0">
                <CaretDown size={22} weight="bold" style={{ color: "var(--app-strong)" }} />
              </motion.div>
            </button>

            <AnimatePresence initial={false}>
              {open && (
                <motion.div key="content" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} className="overflow-hidden">
                  <div className="px-4 pb-4 pt-3 border-t border-gray-200 space-y-2">
                    <button data-testid={`buddy-copy-${tg.code}`} onClick={() => { glow(`copy-${tg.code}`); copyUrl(tg.code); }}
                      style={{ "--glow": AC.copy }} className={`${actionBtn} w-full${gcls(`copy-${tg.code}`)}`}><Copy size={18} weight="bold" color={AC.copy} />{t("copyUrl")}</button>
                    <div className="grid grid-cols-2 gap-2">
                      <WriteTagButton tag={tg} label={t("labelTag")} iconColor={AC.label} style={{ "--glow": AC.label }} className={`${actionBtn}${gcls(`label-${tg.code}`)}`} />
                      {canEdit && (
                        <button data-testid={`buddy-edit-${tg.code}`} onClick={() => { glow(`edit-${tg.code}`); navigate(`/tags/${tg.id}/edit`); }}
                          style={{ "--glow": AC.edit }} className={`${actionBtn}${gcls(`edit-${tg.code}`)}`}><PencilSimple size={18} weight="bold" color={AC.edit} />{t("editBuddy")}</button>
                      )}
                      <button data-testid={`buddy-stats-${tg.code}`} onClick={() => { glow(`stats-${tg.code}`); navigate(`/tags/${tg.id}/stats`); }}
                        style={{ "--glow": AC.stats }} className={`${actionBtn}${gcls(`stats-${tg.code}`)}`}><ChartBar size={18} weight="bold" color={AC.stats} />{t("statsLabel")}</button>
                      <button data-testid={`buddy-preview-${tg.code}`} onClick={() => { glow(`preview-${tg.code}`); window.open(`${url(tg.code)}?preview=1`, "_blank"); }}
                        style={{ "--glow": AC.preview }} className={`${actionBtn}${gcls(`preview-${tg.code}`)}`}><Eye size={18} weight="bold" color={AC.preview} />{t("preview")}</button>
                      {canEdit && tg.status === "ACTIVE" && (
                        <button data-testid={`buddy-block-${tg.code}`} onClick={() => { glow(`block-${tg.code}`); setBlockId(tg.id); }}
                          style={{ "--glow": AC.block }} className={`${actionBtn}${gcls(`block-${tg.code}`)}`}><Warning size={18} weight="bold" color={AC.block} />{t("blockBuddy")}</button>
                      )}
                      {canEdit && tg.status === "BLOCKED" && (
                        <button data-testid={`buddy-unblock-${tg.code}`} onClick={() => { glow(`block-${tg.code}`); unblock(tg.id); }}
                          style={{ "--glow": AC.block }} className={`${actionBtn}${gcls(`block-${tg.code}`)}`}><Warning size={18} weight="bold" color={AC.block} />{t("unblockBuddy")}</button>
                      )}
                      {canEdit && (
                        <button data-testid={`buddy-delete-${tg.code}`} onClick={() => { glow(`del-${tg.code}`); setDeleteId(tg.id); }}
                          style={{ "--glow": AC.del }} className={`${actionBtn}${gcls(`del-${tg.code}`)}`}><Trash size={18} weight="bold" color={AC.del} />{t("deleteBuddy")}</button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {limit && tags.length > limit && (
        <button data-testid="buddy-show-toggle" onClick={() => setShowAll(!showAll)}
          className="sb-btn w-full py-2.5 bg-white font-medium text-sm">
          {showAll ? t("showLess") : `${t("showMore")} (${tags.length - limit})`}
        </button>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>        <AlertDialogContent data-testid="buddy-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{t("deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="buddy-delete-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="buddy-delete-confirm" onClick={confirmDelete} style={{ background: "#EF4444" }}>{t("deleteBuddy")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!blockId} onOpenChange={(o) => !o && setBlockId(null)}>
        <AlertDialogContent data-testid="buddy-block-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">{t("confirmBlock")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirmBlockDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="buddy-block-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="buddy-block-confirm" onClick={confirmBlock} style={{ background: "#EF4444" }}>{t("blockConfirmBtn")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
